/**
 * Documents — the folder structure, in both zones (PAGES.md, Documents [F2] and Documents
 * (arrangement) [F2]).
 *
 * ---------------------------------------------------------------------------------------------
 * THE FILES THEMSELVES ARE `attachments`, AND THIS CONTRACT DOES NOT RESTATE THEM.
 *
 * A document is an attachment whose `ownerType` is `folder`. So uploading into a folder is the
 * upload route that already exists, downloading is the download route that already exists, and the
 * scan status a client renders is the one it already renders. What is new is only the shelf.
 *
 * ---------------------------------------------------------------------------------------------
 * THE TEMPLATE IS DATA, AND PRINCIPLE 9 IS WHY.
 *
 * «Faste mapper som følger av malen: kontrakter, ridere, forsikring, HMS, søknader og tillatelser,
 * presse.» Those six are the DEFAULT, applied when an årgang is created — not a rule. Every one of
 * them can be renamed, moved or deleted afterwards, and an organisation that works differently adds
 * its own. Nothing in the API branches on a template key; it exists so applying the template twice
 * is idempotent.
 *
 * The org-level shelf gets its own three — maler, forsikring, standardkontrakter — from the same
 * mechanism, because the page lists exactly those.
 *
 * ---------------------------------------------------------------------------------------------
 * NOT PAGED, AND THAT IS A MEASURED CHOICE RATHER THAN AN OVERSIGHT.
 *
 * A folder tree is read whole or it cannot be drawn: a page of folders is a tree with holes in it.
 * `MAX_FOLDERS_PER_ZONE` is the ceiling the API refuses to create past, so «read the whole tree»
 * has a bounded cost that a reviewer can see. The FILES inside a folder are paged, because that
 * list has no such bound — a contracts folder is thousands of rows after five years.
 */
import { z } from 'zod';

import { MAX_ATTACHMENT_FILENAME } from './attachments.js';
import { booleanQuerySchema } from './common.js';

/** How many folders one zone may hold. See the header: this is what makes «read it whole» safe. */
export const MAX_FOLDERS_PER_ZONE = 200;

/** How deep the tree may go. Three levels is a structure; ten is a filing system nobody browses. */
export const MAX_FOLDER_DEPTH = 3;

export const MAX_FOLDER_NAME = 80;

/**
 * KONTRAKT- OG ØKONOMIMAPPER (F12, 0661): «Dokumenter i kontrakt- og økonomimapper kan aldri deles.»
 * Merket står på mappa og gjelder alt under den — undermapper og filer. Malens «Kontrakter» og
 * «Standardkontrakter» får `contract` når de lages (utløseren i 0661), og organisasjonen kan merke
 * sine egne mapper. Basen nekter delingen og viser aldri innholdet til en gjest eller lenkeperson.
 */
export const FOLDER_CONFIDENTIAL_KINDS = ['contract', 'economy'] as const;
export const folderConfidentialSchema = z.enum(FOLDER_CONFIDENTIAL_KINDS);
export type FolderConfidential = z.infer<typeof folderConfidentialSchema>;

/**
 * One row of the template. `key` is stable and never shown; `name` is the Norwegian default the
 * organisation is free to change the moment the folder exists.
 */
export interface FolderTemplateRow {
  readonly key: string;
  readonly name: string;
}

/**
 * What a new årgang arrives furnished with. HMS is a folder of documents in this phase — «Full
 * HMS-modul med tiltakslister og hendelseslogg er [senere]» — so risikovurdering, beredskapsplan
 * and evakuering live here as files rather than as their own objects.
 */
export const EDITION_FOLDER_TEMPLATE: readonly FolderTemplateRow[] = [
  { key: 'contracts', name: 'Kontrakter' },
  { key: 'riders', name: 'Ridere' },
  { key: 'insurance', name: 'Forsikring' },
  { key: 'hse', name: 'HMS' },
  { key: 'permits', name: 'Søknader og tillatelser' },
  { key: 'press', name: 'Presse' },
];

/** The organisation's own shelf: «Maler, forsikringer, standardkontrakter, HMS-maler.» */
export const ORGANIZATION_FOLDER_TEMPLATE: readonly FolderTemplateRow[] = [
  { key: 'templates', name: 'Maler' },
  { key: 'insurance', name: 'Forsikring' },
  { key: 'standard-contracts', name: 'Standardkontrakter' },
  { key: 'hse-templates', name: 'HMS-maler' },
];

/**
 * 244.17: MALENS KONTRAKTMAPPER («Kontrakter» og «Standardkontrakter») ER ALLTID KONTRAKTMAPPER. Merket
 * kan ikke tas av (0805 i basen, `confidential_locked` i API-et), så kontraktene kan aldri deles.
 */
export const CONFIDENTIAL_TEMPLATE_KEYS = ['contracts', 'standard-contracts'] as const;
export function folderConfidentialLocked(templateKey: string | null | undefined): boolean {
  return (
    templateKey != null && (CONFIDENTIAL_TEMPLATE_KEYS as readonly string[]).includes(templateKey)
  );
}

export const documentFolderDtoSchema = z
  .object({
    id: z.uuid(),
    /** Null on the organisation's own shelf. */
    editionId: z.uuid().nullable(),
    parentId: z.uuid().nullable(),
    name: z.string(),
    /** Which template row it came from, or null for a folder somebody made. Never shown. */
    templateKey: z.string().nullable(),
    departmentId: z.uuid().nullable(),
    /** Resolved for display, so a tree does not have to join the department list itself. */
    departmentName: z.string().nullable(),
    /** F12: kontrakt- eller økonomimappe, eller null. Merket på mappa selv. */
    confidential: folderConfidentialSchema.nullable().optional(),
    /** F12: mappa eller en mappe over den er merket — da deles ingenting herfra. */
    neverShared: z.boolean().optional(),
    sortOrder: z.number().int(),
    /** How many files are in THIS folder — not counting subfolders. */
    documentCount: z.number().int().nonnegative(),
    /**
     * KOLONNENE «ENDRET» OG «ENDRET AV» I FILUTFORSKEREN (QA 12, #15).
     *
     * En mappe og en fil står i den samme lista, og en kolonne som var tom på annenhver rad ville
     * vært en kolonne man slutter å lese. Navnet er slått opp for visning, akkurat som
     * avdelingsnavnet over, og er null når den som endret mappa ikke lenger er en kontakt.
     */
    updatedAt: z.iso.datetime(),
    updatedByName: z.string().nullable(),
  })
  .meta({ id: 'DocumentFolderDto' });
export type DocumentFolderDto = z.infer<typeof documentFolderDtoSchema>;

/** Not paged, and the header says why: a page of folders is a tree with holes in it. */
export const documentFolderListResponseSchema = z
  .object({ items: z.array(documentFolderDtoSchema) })
  .meta({ id: 'DocumentFolderListResponse' });
export type DocumentFolderListResponse = z.infer<typeof documentFolderListResponseSchema>;

/**
 * Which shelf to read. `editionId` absent means the organisation's own — a distinct request rather
 * than a filter, because the two are different pages with different menu entries.
 */
export const listFoldersQuerySchema = z
  .object({ editionId: z.uuid().optional() })
  .meta({ id: 'ListFoldersQuery' });
export type ListFoldersQuery = z.infer<typeof listFoldersQuerySchema>;

export const createFolderRequestSchema = z
  .object({
    editionId: z.uuid().nullish(),
    parentId: z.uuid().nullish(),
    name: z.string().trim().min(1).max(MAX_FOLDER_NAME),
    departmentId: z.uuid().nullish(),
    confidential: folderConfidentialSchema.nullish(),
  })
  .meta({ id: 'CreateFolderRequest' });
export type CreateFolderRequest = z.infer<typeof createFolderRequestSchema>;

/**
 * Renaming, and moving between departments. NOT moving between parents or zones: a folder that can
 * change its zone can take its files out of the year they belong to, and no page asks for that yet.
 */
export const updateFolderRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(MAX_FOLDER_NAME).optional(),
    departmentId: z.uuid().nullish(),
    confidential: folderConfidentialSchema.nullish(),
  })
  .meta({ id: 'UpdateFolderRequest' });
export type UpdateFolderRequest = z.infer<typeof updateFolderRequestSchema>;

/* =================================================================================================
 * FILUTFORSKEREN (QA-runde 12, punkt #15 — «Dokumenter skal fungere som mappefunksjonen i Teams»).
 *
 * Alt under denne linja er lagt til for den. Det som fantes fra før — lag en mappe, gi den nytt
 * navn, slett en tom en — er uendret over, fordi en utforsker ikke er en ny modell: den er de
 * samme radene med fire handlinger til.
 *
 * FIRE HANDLINGER, OG INGEN AV DEM KRYSSER EN SONE. Flytt, kopier, slett-med-innhold og angre
 * holder seg alle innenfor `edition_id`-en mappa står i. En mappe som kunne bytte sone ville tatt
 * filene sine ut av året de hører til, og det er en handling ingen har bedt om og ingen kan se at
 * de gjorde.
 * ===============================================================================================*/

/**
 * Hvor mange filer én kopieringsoperasjon tar med seg.
 *
 * Kopiering leser bytene og skriver dem på nytt, én fil av gangen, inne i forespørselens egen
 * transaksjon. Uten et tak er «Kopier til» på en mappe med fem års kontrakter en forespørsel som
 * står i minutter og holder en transaksjon åpen hele veien. Taket gjør at grensen er et svar man
 * får med en gang framfor en tidsavbrudd man må gjette på.
 */
export const MAX_COPY_FILES = 200;

/**
 * Hvor noe skal: en mappe i SAMME sone, eller sonens rot.
 *
 * Én form for både «Flytt til» og «Kopier til», fordi spørsmålet de stiller er det samme. Null er
 * roten og er et ekte svar — det er sånn man drar en undermappe ut på øverste nivå.
 */
export const folderTargetRequestSchema = z
  .object({ parentId: z.uuid().nullish() })
  .meta({ id: 'FolderTargetRequest' });
export type FolderTargetRequest = z.infer<typeof folderTargetRequestSchema>;

/** Hvor en FIL skal. En fil ligger alltid i en mappe — det finnes ingen rot for filer. */
export const documentTargetRequestSchema = z
  .object({ folderId: z.uuid() })
  .meta({ id: 'DocumentTargetRequest' });
export type DocumentTargetRequest = z.infer<typeof documentTargetRequestSchema>;

/**
 * «Gi nytt navn» på en fil.
 *
 * Navnet er det eneste en opplasting lot brukeren bestemme, og det eneste som kan være feil uten
 * at bytene er det. Resten av raden — størrelse, sjekksum, lagringsnøkkel — er utledet og kan
 * ikke redigeres.
 */
export const renameDocumentRequestSchema = z
  .object({ filename: z.string().trim().min(1).max(MAX_ATTACHMENT_FILENAME) })
  .meta({ id: 'RenameDocumentRequest' });
export type RenameDocumentRequest = z.infer<typeof renameDocumentRequestSchema>;

/**
 * HVA SOM STÅR I MAPPA, HELE VEIEN NED — tallene bekreftelsesdialogen viser.
 *
 * «En mappe med innhold kan slettes etter bekreftelse som viser antallet filer.» Tallet må være
 * rekursivt: en mappe som er tom selv, men har en undermappe med tretti kontrakter i, er ikke en
 * tom mappe for den som sletter den.
 */
export const folderContentsResponseSchema = z
  .object({
    folderCount: z.number().int().nonnegative(),
    fileCount: z.number().int().nonnegative(),
    byteSize: z.number().int().nonnegative(),
  })
  .meta({ id: 'FolderContentsResponse' });
export type FolderContentsResponse = z.infer<typeof folderContentsResponseSchema>;

/**
 * `?recursive=true` — «ja, ta med det som står i den».
 *
 * Uten flagget oppfører sletting seg nøyaktig som før: en mappe med noe i nekter. Flagget er
 * derfor ikke en bekvemmelighet, det er kvitteringen på at klienten har VIST tallene fra
 * `folderContentsResponseSchema` og fått et ja.
 */
export const deleteFolderQuerySchema = z
  .object({ recursive: booleanQuerySchema.optional() })
  .meta({ id: 'DeleteFolderQuery' });
export type DeleteFolderQuery = z.infer<typeof deleteFolderQuerySchema>;
